// @ts-strict-ignore
import type { ValidationRejection } from "@altostra/type-validations";
import type { BaseTableResultItem, TableResult } from "@tanstack/react-table";

import type { FetchRequestError } from "@lib/errors";
import { FetchInvalidPayloadShapeError } from "@lib/errors";

import type { AllRouteEndpoints } from "../routeTypes";
import type { GetTableFetcherFunctionWithShadowError, InternalTableFetcherFunctionWithShadowError } from "../types";
import type { BaseTableParams, EndpointQueryKey } from "./types";

/**
 * Adapter from react-query fetcher function to a more natural fetcher function for table data.
 *
 * This takes the fetcher function and passes in the query key, minus the root cache key
 * to the function. It also passes the abort signal as an optional last parameter
 *
 * Note that all fetchers must accept only 1 parameter, an object containing all the needed data
 */
export const reactQueryTableFetcherAdapter = <
  CacheKey extends AllRouteEndpoints,
  Args extends BaseTableParams,
  Result extends BaseTableResultItem,
  FullResult extends TableResult<Result>,
  Error extends FetchRequestError = FetchRequestError,
>(
  cacheKey: CacheKey,
  fetcherFunction: InternalTableFetcherFunctionWithShadowError<Error, Args, Result, FullResult>,
  validator?: (value: unknown, rejectionReasons?: (rejection: ValidationRejection) => void) => boolean
): GetTableFetcherFunctionWithShadowError<Error, CacheKey, Args, Result, FullResult> => {
  return async (
    { queryKey: [, args], signal, meta }: EndpointQueryKey<CacheKey, [Args]>,
    customizedFetcher?: Function,
    directCallFetcher = false,
    params?: [Args]
  ) => {
    if (directCallFetcher) {
      return fetcherFunction(signal, ...params);
    }

    const activeFetcherFunction = customizedFetcher || fetcherFunction;
    const result = await activeFetcherFunction(signal, args);
    FetchInvalidPayloadShapeError.validateShape({
      cacheKey,
      result,
      validator,
      logException: meta?.logException as ((error_1: Error) => void) | undefined,
    });
    return result;
  };
};
